import { fetchNearestStores } from '@api/store';
import { getStoreCategories } from '@api/storecategory';
import { TCategory, TGeoLocation, TStore } from '@components/types';
import { useInView } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container } from '@components';
import { SmallStoreCard } from '@components/cards';
import StoreFilters from '@components/widget/store-filters';
import StoreHorizontalController from '@components/widget/store-horizontal-controller';
import StoreSorting from '@components/widget/store-sorting';
import { getLocation } from '@core/geolocation';
import { geoLocationState } from '@recoil/atoms/address';
import { notification } from 'antd';
import Head from 'next/head';
import { FiLoader } from 'react-icons/fi';
import { useRecoilState } from 'recoil';

const Header = dynamic(
  () => import('../../src/components/header').then((mod) => mod.Header),
  {
    ssr: false,
  }
);

const Navbar = dynamic(
  () => import('../../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

const Footer = dynamic(
  () => import('../../src/components/footer').then((mod) => mod.Footer),
  {
    ssr: false,
  }
);

const SORTING_OPTIONS = [
  { value: 'relevence', label: 'Relevence' },
  { value: 'popularity', label: 'Popularity' },
  { value: 'rating', label: 'Rating' },
];

const SERVICE_OPTIONS = [
  { value: 'isDelivery', label: 'Delivery' },
  { value: 'isPickup', label: 'Pickup' },
];

export default function StoresNearYou() {
  const [stores, setStores] = useState<TStore[]>([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isData, setIsData] = useState(true);
  const PAGE_LIMIT = 10;
  const [categoryId, setCategoryId] = useState<null | number>(null);
  const [isDelivery, setIsDelivery] = useState(false);
  const [isPickup, setIsPickup] = useState(false);
  const [geoposition, setGeoposition] = useRecoilState(geoLocationState);
  const [orderingOn, setOrderingOn] = useState<null | string>(
    SORTING_OPTIONS[0].value
  );
  const [myLocation, setMyLocation] = useState(null)

  const [allCategories, setAllCategories] = useState<TCategory[]>([]);
  const [minimumCategories, setMinimumCategories] = useState<TCategory[]>([]);

  const ref = useRef(null);
  const isInView = useInView(ref);

  const [api, contextHolder] = notification.useNotification();

  const checkStale = (location: TGeoLocation) => {
    return Date.now() - location.last_updated > 60000;
  };

  function handleCategoryChange(id: number) {
    if (categoryId === id) setCategoryId(null);
    else setCategoryId(id);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  function handleServiceChange(service: 'isDelivery' | 'isPickup') {
    if (service === 'isDelivery') setIsDelivery(!isDelivery);
    else setIsPickup(!isPickup);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  function handleSortingChange(option: string) {
    if (orderingOn === option) setOrderingOn(null);
    else setOrderingOn(option);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  function handleClearFilters() {
    setCategoryId(null);
    setIsDelivery(false);
    setIsPickup(false);
    setOrderingOn(null);
    setStores([]);
    setIsData(true);
    setSkip(0);
  }

  async function fetchStores() {
    setIsLoading(true);
    try {
      const data = await fetchNearestStores(
        isDelivery,
        isPickup,
        categoryId,
        orderingOn,
        skip,
        PAGE_LIMIT
      );
      if (skip === 0) {
        setStores(data);
      } else {
        if (data.length) setStores([...stores, ...data]);
        else setIsData(false);
      }
    } catch (error) {
      setIsData(false);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchCategories() {
    const data: TCategory[] = await getStoreCategories();
    setAllCategories(data);
    setMinimumCategories(data.slice(0, 10));
  }

  useEffect(() => {
    if (!(geoposition?.latitude > 0) && !(geoposition?.longitude > 0)) {
      getLocation()?.then((location) => {
        setGeoposition({ last_updated: 0, longitude: location?.longitude, latitude: location?.latitude });
      })
    }
  }, [])

  useEffect(() => {
    if (geoposition?.latitude > 0 && geoposition?.longitude > 0) {
      fetchStores();
    }
  }, [isDelivery, isPickup, categoryId, orderingOn, skip, geoposition]);

  useEffect(() => {
    if (isInView && isData && isLoading === false) {
      setSkip((prevSkip) => prevSkip + PAGE_LIMIT);
      setIsLoading(true);
    }
  }, [isInView, isData, isLoading]);


  useEffect(() => {
    if (geoposition?.latitude > 0 && geoposition?.longitude > 0) {
      fetchCategories();
    }
  }, [geoposition]);

  return (
    <Container>
      <Head>
        <title>Stores Near You - Jhattse</title>
        <meta
          name="Keywords"
          content="Local Stores,Local+Online Stores,Stores near you,Local Services,Online Shopping,Jhattse"
        />
        <meta
          name="Description"
          content="Find Stores and services near me whether local or online. Jhattse provides product visiblity, best price discovering and all available deals and offers."
        />
        <link rel="canonical" href={`//store`} />
        <meta property="og:title" content="Stores Near You - Jhattse" />
        <meta
          property="og:description"
          content="Find Stores and services around you whether local or online. Jhattse provides product visiblity, best price discovering and all available deals and offers."
        />
        <meta property="og:url" content={`//store`} />
      </Head>

      <Header />

      <div className="flex flex-col font-manrope gap-2 w-full mx-auto max-w-[1240px] pb-16">
        <div className="hidden px-2 py-8 md:flex items-center font-manrope">
          <Breadcrumb className="text-base text-neutral-400 font-medium py-4 list-none text-breadcrumbs">
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-neutral-800 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink>Stores Near You</BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
        <div className="px-2 h-fit hidden md:flex py-4 space-y-4">
          <h1 className="text-xl text-neutral-800 font-extrabold">
            Stores Near You
          </h1>
        </div>

        {geoposition?.latitude > 0 && geoposition?.longitude > 0 ? <div className="w-full h-fit grid grid-cols-1 md:grid-cols-4  ">
          <StoreFilters
            categoryId={categoryId}
            handleCategoryChange={handleCategoryChange}
            handleClearFilters={handleClearFilters}
            servicesFilter={true}
            isDelivery={isDelivery}
            isPickup={isPickup}
            handleServiceChange={handleServiceChange}
            allCategories={allCategories}
            minimumCategories={minimumCategories}
          />

          <div className="md:col-span-3 px-2">
            <StoreSorting
              options={SORTING_OPTIONS}
              orderingOn={orderingOn}
              handleSortingChange={handleSortingChange}
            />

            {/* to control sorting and filtering on smaller devices */}
            <StoreHorizontalController
              serviceOptions={SERVICE_OPTIONS}
              sortingOptions={SORTING_OPTIONS}
              handleServiceChange={handleServiceChange}
              handleSortingChange={handleSortingChange}
              categoryId={categoryId}
              categories={allCategories}
              handleCategoryChange={handleCategoryChange}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-2">
              {stores?.map((store) => (
                <SmallStoreCard key={store.id} store={store} />
              ))}
              {isLoading && (
                <div className="px-12 py-6 flex justify-center">
                  <FiLoader className="w-8 h-8 text-neutral-400 animate-spin" />
                </div>
              )}

              <div ref={ref} className="w-full h-10" />
            </div>
          </div>
        </div> : <div className="text-base text-error-400 px-2">
          Please allow location access.
        </div>}
      </div>
      <Navbar />
      <Footer />
    </Container>
  );
}
